/**
 * The site-wide import promise (§27a), shown on every page by
 * `organisms/ImportPromiseBand`.
 *
 * It exists because the business was being described by its pages rather than
 * by itself: a visitor landing on /tax-calculator or /reviews could read the
 * whole page without learning that Kabex *imports to order* — that there is no
 * showroom floor to walk and no bond to pick from, and that the car they get
 * is the one they specify. That is the single most important thing about the
 * service and the one thing no page said twice.
 *
 * One line, repeated verbatim everywhere, on purpose. A band that reworded
 * itself per page would be eight weaker versions of the same claim; brand
 * recall comes from the same sentence arriving again, not from variety. Pages
 * may override the copy (see the band's props) but none currently do.
 *
 * PENDING KABEX (§59): the wording below is ours, not supplied. It is built
 * from already-approved facts — the §7 services, the §11 differentiators and
 * the §10 journey — and states nothing they do not, but the sentences
 * themselves have not been signed off. The three proof points are deliberately
 * things the site can already evidence elsewhere; do not add a claim here that
 * no page behind it supports.
 */

import { type ContentItem } from './site';

export type ImportPromiseCta = { label: string; to: string };

export const importPromise = {
  eyebrow: 'Sourced to order',
  title: 'Import the car you choose with Kabex Motors',
  text: 'Tell us the make, model, year and budget. We’ll source the vehicle to order, and your written quotation will state the CIF destination and services included.',

  /**
   * Three, not five. The band is an interruption between a page's content and
   * its quotation close, and an interruption that takes a whole screen stops
   * being one. Each answers the objection that follows the headline: *will it
   * be the car I asked for*, *what will it really cost*, and *who handles the
   * rest*.
   */
  proofs: [
    {
      icon: 'search',
      title: 'Your specification, confirmed',
      text: 'We confirm the make, model, year, mileage and trim in writing before payment.',
    },
    {
      icon: 'calculator',
      title: 'Know the costs up front',
      text: 'Review the CIF price and URA assessment before the vehicle arrives.',
    },
    {
      icon: 'route',
      title: 'Destination agreed in advance',
      text: 'Your order records where Kabex will hand over the vehicle and which services are included.',
    },
  ] satisfies ContentItem[],

  /**
   * The same promise at ribbon length, for the fixed bar above the header.
   *
   * Two clauses rather than one sentence, because the bar is one line tall at
   * every width and a phone has room for about five words. `promise` is not
   * an abbreviation of the whole — it is the half that still works alone,
   * while `proof` is what a wider screen has room to add. Proof that has to
   * be read at 13px on a 390px screen is not proof.
   *
   * Split here rather than in the component so the ribbon can bring them in
   * one after the other (see `atoms/ribbonMotion.ts`): a single string could
   * only ever arrive all at once.
   *
   * The clause is deliberately *positive*. The band below the fold opens on
   * the negation ("No showroom. No bond.") because it has the room to then
   * explain what that buys. A ribbon does not: a stranger meeting "no
   * showroom, no bond" in the first second, with no context either side, is
   * being told what Kabex lacks before they know what it is. So the ribbon
   * states the same difference as a gain — you name the car, we bring that
   * car — and leaves the contrast to the page.
   */
  banner: {
    /** Carries the bar on its own; the only part a phone has room for. */
    promise: 'Import your dream car with Kabex Motors.',
    /** The proof, added from md up, where there is width for a second clause. */
    proof: 'Sourced to your exact spec. Destination agreed in advance.',
    action: 'Start my import',
    /** Names the landmark for screen readers; never shown. */
    label: 'Importing with Kabex Motors',
    dismiss: 'Hide this message',
  },

  /**
   * /import-a-car and not /quote. The quotation band directly below this one
   * already owns /quote, and two bands pointing at the same form would make
   * this one redundant; the promise is about *how* Kabex works, so it sends
   * the reader to the page that explains and starts that process.
   */
  cta: { label: 'Start my import', to: '/import-a-car' } satisfies ImportPromiseCta,
} as const;
